import { supabase } from '../supabaseClient';
import { useEffect, useState } from 'react';

const Dashboard = () => {
  const [stats, setStats] = useState({ totalUser: 0, totalSearch: 0, totalLimit: 0 });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const ignoredBranches = ["BranchName1", "BranchName2"]; // branches to ignore

  const fetchStats = async () => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data?.session?.access_token;

      const query = new URLSearchParams();
      if (fromDate) query.append('fromDate', fromDate);
      if (toDate) query.append('toDate', toDate);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/statictis/users?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const fetchUsers = async (page = currentPage, size = itemsPerPage) => {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data?.session?.access_token;

      const query = new URLSearchParams();
      query.append('pageSize', size);
      query.append('offset', (page - 1) * size);
      if (fromDate) query.append('createdAtFrom', fromDate);
      if (toDate) query.append('createdAtTo', toDate);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      setUsers(data.users || []);
      const filtered = (data.users || []);
      setFilteredUsers(filtered);
      setHasMore(data.hasMore);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, []);

  const exportCSV = () => {
    const csvContent = 'data:text/csv;charset=utf-8,' +
      ['Branch,Email,Limit,Search,Created,EndDate', ...filteredUsers?.map(u =>
        `${u.branchName},${u.email},${u.totalLimit},${u.totalSearch},${new Date(u.createdAt).toLocaleDateString()},${u.endDate ? new Date(u.endDate).toLocaleDateString() : ''}`
      )].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'users.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleItemsPerPageChange = (e) => {
    const newSize = Number(e.target.value);
    setItemsPerPage(newSize);
    setCurrentPage(1);
    fetchUsers(1, newSize);
  };

  const handlePrevPage = () => {
    const newPage = Math.max(currentPage - 1, 1);
    setCurrentPage(newPage);
    fetchUsers(newPage);
  };

  const handleNextPage = () => {
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    fetchUsers(newPage);
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Dashboard Overview</h1>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{ padding: '8px' }}
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{ padding: '8px' }}
        />
        <button
          onClick={() => {
            fetchStats();
            fetchUsers(1, itemsPerPage);
            setCurrentPage(1);
          }}
          style={{ padding: '8px 12px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px' }}
        >
          Apply Filter
        </button>
      </div>
      <div style={styles.grid}>
        <StatCard title="Total Users" value={stats.totalUser} />
        <StatCard title="Total Searches" value={stats.totalSearch} />
        <StatCard title="Total Limit" value={stats.totalLimit} />
      </div>
      <h2>User List</h2>
      <button onClick={exportCSV} style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '4px' }}>
        Export CSV
      </button>
      <div style={{ marginBottom: '10px' }}>
        <label>
          Items per page:
          <select value={itemsPerPage} onChange={handleItemsPerPageChange} style={{ marginLeft: '8px', padding: '4px' }}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </label>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>#</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Branch</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Email</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Limit</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Search</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Price</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Paid</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>Created At</th>
            <th style={{ border: '1px solid #ccc', padding: '8px' }}>End Date</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(filteredUsers) && filteredUsers.map((user, index) => (
            <tr key={user.id}>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                {(currentPage - 1) * itemsPerPage + index + 1}
              </td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{user.branchName}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{user.email}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{user.totalLimit}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{user.totalSearch}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                {user.price != null
                  ? Number(user.price).toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
                  : 'N/A'}
              </td>              
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                <span
                  style={{
                    backgroundColor: user.isPaid ? '#d1fae5' : '#fee2e2',
                    color: user.isPaid ? '#065f46' : '#991b1b',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontWeight: 'bold'
                  }}
                >
                  {user.isPaid ? 'Yes' : 'No'}
                </span>
              </td>              
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{new Date(user.createdAt).toLocaleDateString()}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{user.endDate ? new Date(user.endDate).toLocaleDateString() : 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
        <button onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
        <span>Page {currentPage}</span>
        <button onClick={handleNextPage} disabled={!hasMore}>Next</button>
      </div>
    </div>
  );
};

const StatCard = ({ title, value }) => (
  <div style={styles.card}>
    <h3 style={styles.cardTitle}>{title}</h3>
    <p style={styles.cardValue}>{value}</p>
  </div>
);

const styles = {
  container: {
    padding: '20px'
  },
  title: {
    marginBottom: '20px'
  },
  grid: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  },
  card: {
    flex: '1 1 200px',
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: '#f3f4f6',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
    textAlign: 'center'
  },
  cardTitle: {
    fontSize: '16px',
    color: '#374151'
  },
  cardValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginTop: '8px',
    color: '#111827'
  }
};

export default Dashboard;